<template>
    <Teleport to="body">
        <Transition name="modal">
            <div v-if="isOpen"
                 @click.self="$emit('close')"
                 class="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center p-4 z-[51]"
            >
                <div class="max-w-md w-full bg-white rounded-3xl shadow-xl overflow-hidden relative max-h-[90vh] overflow-y-auto">

                    <!-- Close Button -->
                    <button
                        @click="$emit('close')"
                        class="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/20 hover:bg-black/30 transition-colors"
                    >
                        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>

                    <!-- Header Section -->
                    <div class="bg-amber-500 px-8 py-6 text-center">
                        <img
                            :src="STAR_ICON_URL"
                            alt="Star"
                            class="h-14 mx-auto mb-3 drop-shadow-lg"
                        />
                        <h2 class="text-3xl font-bold text-white mb-1.5">
                            Out of stars?
                        </h2>
                        <p class="text-white/95 text-sm leading-relaxed max-w-xs mx-auto">
                            Get instant stars + unlimited access to 11 premium sites
                        </p>
                    </div>

                    <div class="flex items-center justify-center gap-8 py-6 bg-white">
                        <div class="text-center">
                            <div class="text-4xl font-bold text-gray-900 mb-1">25-40</div>
                            <div class="text-xs text-gray-500">stars/month</div>
                        </div>
                        <div class="text-3xl text-amber-500 font-light">+</div>
                        <div class="text-center">
                            <div class="text-4xl font-bold text-gray-900 mb-1">11</div>
                            <div class="text-xs text-gray-500">bonus sites</div>
                        </div>
                    </div>

                    <!-- Join Options -->
                    <div class="px-6 pb-3 space-y-3">
                        <button
                            v-for="option in joinOptions"
                            :key="option.name"
                            @click="selectedOption = option.name"
                            :class="[
                                'relative w-full p-5 rounded-2xl border-2 transition-all text-left hover:shadow-2xl active:scale-[0.99]',
                                selectedOption === option.name
                                    ? 'border-amber-500 bg-gray-900 ring-4 ring-amber-500/20'
                                    : 'border-gray-200 bg-white opacity-70 hover:opacity-100'
                            ]"
                        >
                            <div v-if="option.name === 'Annual'" class="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500 text-black text-xs font-bold rounded-full shadow-lg">
                                BEST VALUE • SAVE 38%
                            </div>

                            <div class="flex items-center justify-between mt-1">
                                <div class="flex-1">
                                    <div class="flex items-baseline gap-1 mb-2">
                                        <span :class="['text-5xl font-bold', selectedOption === option.name ? 'text-white' : 'text-gray-900']">
                                            {{ option.dailyDisplayPrice }}
                                        </span>
                                        <span :class="['text-lg', selectedOption === option.name ? 'text-gray-300' : 'text-gray-600']">
                                            /day
                                        </span>
                                    </div>
                                    <div :class="['text-xs font-semibold uppercase tracking-wide mb-2', selectedOption === option.name ? 'text-gray-400' : 'text-gray-500']">
                                        {{ option.name }}
                                    </div>

                                    <div class="space-y-1.5">
                                        <div v-for="content in option.includedContent" :key="content" class="flex items-center gap-1.5">
                                            <svg :class="['w-3.5 h-3.5 flex-shrink-0', selectedOption === option.name ? 'text-amber-500' : 'text-gray-400']" fill="currentColor" viewBox="0 0 20 20">
                                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                                            </svg>
                                            <span :class="['text-xs', selectedOption === option.name ? 'text-white' : 'text-gray-900']">
                                                {{ content }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </button>

                        <!-- Billing Details -->
                        <div class="text-center pt-2 pb-1">
                            <p class="text-xs text-gray-500">
                                Annual plan billed at {{ yearlyTotalPrice }}/year • Monthly plan billed at {{ monthlyTotalPrice }}/month
                            </p>
                        </div>

                        <!-- CTA Button -->
                        <button
                            @click="handleJoin"
                            class="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg rounded-xl transition-all shadow-lg hover:shadow-xl active:scale-[0.99] mt-2"
                        >
                            Upgrade to GOLD
                        </button>
                    </div>

                    <!-- Bonus Sites Section -->
                    <div class="px-6 pb-6">
                        <div class="mb-3">
                            <div class="text-xs font-semibold text-gray-900 mb-1">Included with every plan</div>
                            <div class="text-xs text-gray-500">Unlimited streaming on 11 premium sites</div>
                        </div>

                        <div class="grid grid-cols-2 gap-2 mb-4">
                            <div v-for="site in bonusSites" :key="site" class="p-3 bg-gray-50 rounded-lg">
                                <span class="text-xs text-gray-900 font-medium">{{ site }}</span>
                            </div>
                        </div>

                        <!-- Trust Indicators -->
                        <div class="flex items-center justify-center gap-3 text-xs text-gray-500 pt-4">
                            <span class="flex items-center gap-1">
                                <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
                                </svg>
                                Secure Payment
                            </span>
                            <span class="text-gray-300">•</span>
                            <span>Instant Access</span>
                            <span class="text-gray-300">•</span>
                            <span>No Commitment</span>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import {ref, defineEmits, computed} from 'vue';
import {usePage} from "@inertiajs/vue3";
import {trackAuditionAnalyticsEvent} from "@/Helpers/auditions";
import {trackEvent} from "@/Helpers/analytics";

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true
    },
    source: {
        type: String,
        default: ''
    },
    audition: {
        type: Object,
        default: null
    }
})

const emit = defineEmits(['close'])

const STAR_ICON_URL = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const selectedOption = ref('Annual')
const joinOptionsData = usePage().props.joinOptions

const joinOptions = computed(() => {
    const options = []
    if (joinOptionsData.button_yearlyGold) {
        options.push({
            name: joinOptionsData.button_yearlyGold.data.name,
            stars: joinOptionsData.button_yearlyGold.data.stars,
            stars_text: joinOptionsData.button_yearlyGold.data.stars_text,
            dailyDisplayPrice: `$${joinOptionsData.button_yearlyGold.data.formatted_daily_amount}`,
            fullDisplayPrice: `$${joinOptionsData.button_yearlyGold.data.formatted_initial_amount}`,
            period: joinOptionsData.button_yearlyGold.data.period,
            description: joinOptionsData.button_yearlyGold.data.included_content,
            includedContent: joinOptionsData.button_yearlyGold.data.included_content,
            url: joinOptionsData.button_yearlyGold.url,
            type: 'yearlyGold'
        })
    }
    if (joinOptionsData.button_monthlyGold) {
        options.push({
            name: joinOptionsData.button_monthlyGold.data.name,
            stars: joinOptionsData.button_monthlyGold.data.stars,
            stars_text: joinOptionsData.button_monthlyGold.data.stars_text,
            dailyDisplayPrice: `$${joinOptionsData.button_monthlyGold.data.formatted_daily_amount}`,
            fullDisplayPrice: `$${joinOptionsData.button_monthlyGold.data.formatted_initial_amount}`,
            period: joinOptionsData.button_monthlyGold.data.period,
            description: joinOptionsData.button_monthlyGold.data.included_content,
            includedContent: joinOptionsData.button_monthlyGold.data.included_content,
            url: joinOptionsData.button_monthlyGold.url,
            type: 'monthlyGold'
        })
    }

    return options;
})
const selectedOptionData = computed(() =>
    joinOptions.value.find((t) => t.name === selectedOption.value)
)
const yearlyTotalPrice = computed(() => `$${joinOptionsData.button_yearlyGold.data.formatted_initial_amount}`)
const monthlyTotalPrice = computed(() => `$${joinOptionsData.button_monthlyGold.data.formatted_initial_amount}`)

const handleJoin = () => {
    const joinUrl = selectedOptionData.value.url +
        (selectedOptionData.value.url.includes('?') ?
            `&custom8=${props.source}` :
            `?custom8=${props.source}`)

    console.log('++ JoinOptions: From ' + props.source + ' Redirecting to', joinUrl);

    localStorage.setItem('blue_join_options_source', props.source);
    localStorage.setItem('blue_join_options_join_type', selectedOptionData.value.type);
    localStorage.setItem('blue_join_options_url', joinUrl);

    if (props.audition) {

        LTIAnalytics.setCookie("gold_upgrade_source", props.source);
        LTIAnalytics.setCookie("gold_upgrade_video_id", props.audition.id ?? '');
        trackAuditionAnalyticsEvent('Biller Join Button Clicked', props.audition, {
            store_in_local_storage: true,
            local_storage_key: 'blue_join_options_video_props',
            append_data: {
                source: props.source,
                join_type: selectedOptionData.value.type
            }
        });
    } else {
        trackEvent('Biller Join Button Clicked', {
            source: props.source,
            join_type: selectedOptionData.value.type
        });
    }

    window.location.href = joinUrl;
}

const bonusSites = [
    "Tonight's Girlfriend®",
    "Naughty America XR",
    "Real Girls Now",
    "Virtual Sex World",
    "RealPornstars VR",
    "StarsVideo.TV",
    "Dream Lover",
    "Naughty America 3D",
    "Cream",
    "Bridge",
    "ThunderCock.com"
];
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-active .max-w-md,
.modal-leave-active .max-w-md {
    transition: transform 0.3s ease;
}

.modal-enter-from .max-w-md,
.modal-leave-to .max-w-md {
    transform: scale(0.95);
}

/* Override text-sm to be 12px instead of default 14px */
.text-xs {
    font-size: 12px;
}
</style>
